import type { AdminOverview } from '@steado/admin-contract/overview';
import { ChevronRightIcon } from 'lucide-react';

import { Panel } from '@/components/panel/panels';
import { cn } from '@/lib/utils';

/**
 * The lists a plan-health figure opens into.
 *
 * `generating` goes to the **users** list and the rest to **plan operations**,
 * which is not an inconsistency: the first counts people with a week in flight,
 * the rest count generation runs, and the two populations are genuinely
 * different.
 */
export type PlanHealthScope = 'generating' | 'failed' | 'residual';

interface PlanHealthPanelProps {
  readonly overview: AdminOverview;
  readonly onOpen: (scope: PlanHealthScope) => void;
}

/**
 * The four things that can be wrong with plan generation.
 *
 * Counts rather than rates: "12 failed" is a number of people to go and look
 * at, and a percentage of a population nobody has in mind is not. Three of the
 * four open the list that explains them, because a health card whose numbers
 * cannot be opened is a card that gets read once and then ignored.
 */
export function PlanHealthPanel({ overview, onOpen }: PlanHealthPanelProps) {
  const days = overview.windowDays;

  return (
    <Panel title="Plan generation" bleed>
      <ul>
        <Row
          count={overview.plans.generating}
          label="Generating right now"
          onOpen={() => {
            onOpen('generating');
          }}
        />
        <Row
          count={overview.plans.failedInWindow}
          label={`Failed in ${String(days)} days`}
          tone="err"
          onOpen={() => {
            onOpen('failed');
          }}
        />
        <Row
          count={overview.plans.approximatedInWindow}
          label={`Committed with a residual in ${String(days)} days`}
          tone="warn"
          onOpen={() => {
            onOpen('residual');
          }}
        />
        {/*
          No link: the solver relaxing its bounds is recorded on the plan week,
          and `PLAN_RUN_STATUSES` has no status for it, so there is no list to
          open. A row that navigated nowhere would be worse than one that does
          not offer to — the missing chevron is the whole of the signal.
        */}
        <Row
          count={overview.plans.relaxedInWindow}
          label={`Targets relaxed in ${String(days)} days`}
          tone="warn"
        />
      </ul>
    </Panel>
  );
}

interface RowProps {
  readonly count: number;
  readonly label: string;
  readonly tone?: 'warn' | 'err';
  readonly onOpen?: () => void;
}

function Row({ count, label, tone, onOpen }: RowProps) {
  const body = (
    <>
      <span
        className={cn(
          'inline-flex h-5 min-w-[34px] shrink-0 items-center justify-center rounded-[10px] px-1.5 font-mono text-xs font-semibold tabular-nums',
          // Zero is the healthy reading for three of these four, and painting
          // it red teaches an operator to stop reading the colour.
          count === 0 || tone === undefined
            ? 'bg-mute-bg text-mute-fg'
            : tone === 'err'
              ? 'bg-err-bg text-err-fg'
              : 'bg-warn-bg text-warn-fg',
        )}
      >
        {count}
      </span>
      <span className="min-w-0 text-[13px] font-medium text-pretty text-foreground">{label}</span>
    </>
  );

  if (onOpen === undefined) {
    return <li className="flex items-center gap-3 border-t border-border px-[18px] py-2.5">{body}</li>;
  }

  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className="flex w-full cursor-pointer items-center gap-3 border-t border-border px-[18px] py-2.5 text-left hover:bg-secondary"
      >
        {body}
        <ChevronRightIcon aria-hidden="true" className="ml-auto size-4 shrink-0 text-input" />
      </button>
    </li>
  );
}
